import qs from 'qs';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import VendorsAPI, {
  Application,
  FormattedApplication,
  InitVendorApplicationData,
  Vendor,
} from '../Api/vendor';
import VendorTypeLabels, { VendorType } from '../Constants/vendor-types';

const getVendorType = (vendor: Vendor) => {
  const existingVendor = vendor.existing_vendor ? 'Existing' : 'New';
  const vendorType = VendorTypeLabels[vendor.merch_type as VendorType];
  if (vendorType)
    return `${existingVendor} - ${VendorTypeLabels[vendor.merch_type as VendorType]}`;
  return `${existingVendor}`;
};

export const useVendorInitApplication = ({
  successCb,
}: {
  successCb: () => void;
}) =>
  useMutation(
    (formData: InitVendorApplicationData) =>
      VendorsAPI.initVendorApplication(formData),
    {
      onError: (error: { errors?: unknown }) => {
        toast(`Can not init vendor. ${JSON.stringify(error.errors) || error}`, {
          type: 'error',
        });
      },
      onSuccess: () => {
        toast('Vendor successfully initiated', { type: 'success' });
        if (successCb) {
          successCb();
        }
      },
    },
  );

export const useGetVendorsApplications = ({
  params,
}: {
  params?: unknown;
} = {}) =>
  useQuery(
    ['vendor-applications', params],
    () => VendorsAPI.getApplications(qs.stringify(params)),
    {
      keepPreviousData: true,
      onError: (error: { errors?: unknown }) => {
        toast(
          `Can not retrieve vendors applications. ${
            JSON.stringify(error.errors) || error
          }`,
          {
            type: 'error',
          },
        );
      },
      select: (data: { applications: Application[]; count: number }) => {
        const formattedData: FormattedApplication[] = data.applications.map(
          (application: Application) => ({
            vendor_name:
              application.vendor.legal_name || application.vendor.company_name,
            vendor_id: application.vendor.id,
            contact: application.vendor.contact,
            vendor_type: getVendorType(application.vendor),
            application_stage: application.status,
            vendor_application_status: application.vendor.application_status,
            vendor_application_archived_at:
              application.vendor.application_archived_at,
            stage_status: application.current_step,
            last_updated_at: application.updated_at,
            inviteTokens: application.vendor.inviteTokens || [],
          }),
        );
        return { formattedData, count: data.count };
      },
    },
  );

export const useRefreshTokenVendorApplication = () =>
  useMutation(
    (refreshTokenId: string) =>
      VendorsAPI.refreshTokenVendorApplication(refreshTokenId),
    {
      onError: (error: { errors?: unknown }) => {
        toast(
          `Failed to refresh Token: ${JSON.stringify(error.errors) || error}`,
          {
            type: 'error',
          },
        );
      },
      onSuccess: () => {
        toast('Token successfully refreshed', { type: 'success' });
      },
    },
  );

export default useVendorInitApplication;
